/* eslint-disable indent */
/* eslint-disable no-confusing-arrow */
/* eslint-disable css-modules/no-unused-class */
import React, { useState, useEffect } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import Tabs from 'ui/Tabs/Tabs';
import Button, { Sizes } from 'ui/Button/Button';
import Loader from 'ui/Loader/Loader';
import MobileSubtitle from 'ui/MobileSubtitle/MobileSubtitle';
import identity from 'utils/identity';
import { IApiResponse } from 'utils/models';
import api from 'api/api';
import {
  IAdditionalData,
  IBanksResponse,
  INetworksResponse,
  IBillersResponse,
  IBeneficiariesMap,
  MainTabsType,
  IInstantBankResponse,
  Category,
} from './Beneficiaries.model';
import AddBeneficiaryForm from './AddBeneficiaryForm';

import MobileMoneyBen from './tabs/MobileMoneyBen/MobileMoneyBen';
import TransfersBen from './tabs/TransfersBen/TransfersBen';
import PrepaidCardBen from './tabs/PrepaidCardBen/PrepaidCardBen';

import styles from './Beneficiaries.module.css';

const beneficiariesMap: IBeneficiariesMap[] = [
  {
    key: 'FABTransfer',
    label: 'Transfers',
    path: 'transfer',
    filters: [
      { name: 'First Atlantic Bank', code: 'FAB' },
      { name: 'Other Bank', code: '' },
      // { name: 'Bulk transfer', code: '' },
    ],
    getFilterCb: (filterCode) =>
      filterCode === 'FAB'
        ? ({ bankName }) => bankName === 'First Atlantic Bank'
        : ({ bankName }) => bankName !== 'First Atlantic Bank',
    getFieldsData: (ben, otherBank) => {
      const data = [{ label: 'Account Number', value: ben.accountNumber }];
      if (otherBank) {
        data.unshift({ label: 'Bank', value: ben.bankName });
      }
      return data;
    },
  },
  {
    key: 'mobilemoney',
    label: 'Mobile Money',
    path: 'mobilemoney',
    filters: [
      { name: 'Single Beneficiaries', code: 'MTN' },
      { name: 'Bulk Beneficiaries', code: 'VODA' },
    ],
    getFilterCb:
      (filterCode) =>
      ({ networkCode }) =>
        networkCode === filterCode,
    getFieldsData: (ben) => [
      { label: 'Network', value: ben.networkCode },
      { label: 'Phone Number', value: ben.phoneNumber },
    ],
  },
  {
    key: 'prepaidcard',
    label: 'Prepaid Card',
    path: 'prepaidcard',
    filters: [
      { name: 'Single Beneficiaries', code: 'MTN' },
      { name: 'Bulk Beneficiaries', code: 'VODA' },
    ],
    getFilterCb: () => identity,
    getFieldsData: (ben) => [
      { label: 'Customer ID', value: ben.cardCustomerId },
      { label: 'Card’s last 4 digits', value: ben.last4digit },
    ],
  },
];

const mainTabsOptions = beneficiariesMap.map(({ label }) => ({ label }));

enum States {
  browsing = 'browsing',
  editing = 'editing',
  adding = 'adding',
  viewing = 'viewing',
}
const excludedNetworks = ['Surfline', 'Glo', 'Busy'];
const Beneficiaries = () => {
  const [state, setState] = useState<States>(States.browsing);
  const [initialCategoryValue, setInitialCategoryValue] = useState<Category>('FABTransfer');
  const changeInitialCategory = (value: Category) => setInitialCategoryValue(value);

  const [activeMainTab, setActiveMainTab] = useState<MainTabsType>(0);

  const [loadingData, setLoadingData] = useState(false);

  const [additionalData, setAdditionalData] = useState<IAdditionalData>({
    instantBanks: [],
    nonInstantBanks: [],
    networks: [],
    billers: [],
  });

  const downMdScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  // Check check on render tab view (mobile or web)
  useEffect(() => {
    if (downMdScreen) {
      setActiveMainTab(false);
    } else {
      setActiveMainTab(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downMdScreen]);

  useEffect(() => {
    setLoadingData(true);

    const fetchNonInstantBanks = () =>
      api
        .get('/reference/banks', {}, true, false)
        .then((response: IBanksResponse) => {
          setAdditionalData((prev) => ({ ...prev, nonInstantBanks: response.banks }));
        })
        .catch((error: IApiResponse) => {
          console.error(error.description);
        });

    const fetchInstantBanks = () =>
      api
        .get('transfer/otherbank/institutions', {}, true, false)
        .then((response: IInstantBankResponse) => {
          setAdditionalData((prev) => ({ ...prev, instantBanks: response.banks }));
        })
        .catch((error: IApiResponse) => {
          console.error(error.description);
        });
    const fetchNetworks = () =>
      api
        .get('/billspayment/networks', {}, true, false)
        .then((response: INetworksResponse) => {
          setAdditionalData((prev) => ({
            ...prev,
            networks: response.networks.filter(({ name }) => !excludedNetworks.includes(name)),
          }));
        })
        .catch((error: IApiResponse) => {
          console.error(error.description);
        });

    const fetchBillers = () =>
      api
        .get('/billspayment/billers', {}, true, false)
        .then((response: IBillersResponse) => {
          setAdditionalData((prev) => ({ ...prev, billers: response.billers }));
        })
        .catch((error: IApiResponse) => {
          console.error(error.description);
        });

    Promise.all([fetchInstantBanks(), fetchNonInstantBanks(), fetchNetworks(), fetchBillers()]).finally(() => {
      setLoadingData(false);
    });
  }, []);

  const mobileCategoryMenuOpen = downMdScreen && activeMainTab === false;

  const closeForm = () => setState(States.browsing);
  const openAddForm = () => setState(States.adding);

  const handleChangeMainTab = (_event: React.ChangeEvent<unknown>, tabIndex: MainTabsType) => {
    if (tabIndex === 0) {
      setInitialCategoryValue('FABTransfer');
    }
    if (tabIndex === 1) {
      setInitialCategoryValue('mobilemoney');
    }
    if (tabIndex === 2) {
      setInitialCategoryValue('prepaidcard');
    }
    setActiveMainTab(tabIndex);
    setState(States.browsing);
  };

  const openCategoryMenu = () => {
    setState(States.browsing);
    setActiveMainTab(false);
  }; // mobile only

  const renderTitle = () => {
    if (activeMainTab === false) {
      return (
        <div>
          <h2 className={styles.categoryMenuTitle}>Beneficiaries</h2>
        </div>
      );
    }

    return (
      <NavigationTitle to="beneficiaries" onClick={openCategoryMenu} layoutType={LayoutType.dashboard}>
        {downMdScreen ? `Beneficiaries: ${beneficiariesMap[activeMainTab].label}` : 'Beneficiaries'}
      </NavigationTitle>
    );
  };

  if (state === States.adding) {
    return (
      <AddBeneficiaryForm onClose={closeForm} additionalData={additionalData} initCategory={initialCategoryValue} />
    );
  }

  return (
    <DashboardCard className={styles.positionRelative} fullHeight>
      {renderTitle()}
      {loadingData ? (
        <Loader className="positionCenter" />
      ) : (
        <>
          {downMdScreen && activeMainTab === false && <MobileSubtitle>Select category</MobileSubtitle>}
          {!loadingData && <Tabs value={activeMainTab} options={mainTabsOptions} onChange={handleChangeMainTab} />}
          {!loadingData && activeMainTab === 0 && (
            <TransfersBen
              instantBanks={additionalData.instantBanks}
              nonInstantBanks={additionalData.nonInstantBanks}
              changeInitialCategory={changeInitialCategory}
            />
          )}
          {!loadingData && activeMainTab === 1 && <MobileMoneyBen networks={additionalData.networks} />}
          {!loadingData && activeMainTab === 2 && <PrepaidCardBen />}

          {(!downMdScreen || mobileCategoryMenuOpen) && (
            <div className={styles.addButtonWrapper}>
              <Button size={downMdScreen ? Sizes.big : Sizes.small} fullWidth onClick={openAddForm}>
                Add Beneficiary
              </Button>
            </div>
          )}
        </>
      )}
    </DashboardCard>
  );
};

export default Beneficiaries;
